import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { Anchor } from '@atlaskit/primitives';

import { useCopyToClipboard } from '@confluence/copy-content-link';

import { useBulkCreateFlagsContainer } from '../providers/AiBulkNavigationContextProvider';
import { messages } from '../messages';
import type { CreateIssueResponse } from '../__types__/apiUtils';

import { useGetJiraIssuesLink } from './useGetJiraIssuesLink';

type SingleIssueCreatedFlagPayload = {
	issueKey: string;
	summary: string;
};

const FLAG_ID = 'bulk-issue-create-flags';

export const useBulkCreateFlags = () => {
	const { formatMessage } = useIntl();
	const flags = useBulkCreateFlagsContainer();
	const getJiraIssuesLink = useGetJiraIssuesLink();
	const { copyToClipboard } = useCopyToClipboard();

	const triggerSingleIssueCreatedFlag = useCallback(
		(payload: SingleIssueCreatedFlagPayload) => {
			const link = getJiraIssuesLink([{ key: payload.issueKey }]);
			void flags?.showFlag({
				id: FLAG_ID,
				type: 'success-circle',
				title: formatMessage(messages.sidePanelSingleIssueCreateSuccessFlagTitle, {
					issueKey: payload.issueKey,
					issueSummary: payload.summary,
					link: (chunks: ReactNode[]) => (
						<Anchor href={link} target="_blank">
							{chunks}
						</Anchor>
					),
				}),
				close: 'auto',
			});
		},
		[flags, formatMessage, getJiraIssuesLink],
	);

	const triggerMultipleIssuesCreatedFlag = useCallback(
		(payload: CreateIssueResponse[]) => {
			const href = getJiraIssuesLink(payload);
			void flags?.showFlag({
				id: FLAG_ID,
				type: 'success-circle',
				title: formatMessage(messages.sidePanelMultipleIssueCreateSuccessFlagTitle, {
					issueCount: payload.length,
				}),
				close: 'auto',
				actions: [
					{
						content: formatMessage(messages.sidePanelMultipleIssueCreateFlagViewIssue, {
							issueCount: payload.length,
						}),
						href,
						target: '_blank',
					},
					{
						content: formatMessage(messages.sidePanelMultipleIssueCreateFlagCopyLink),
						onClick: () => void copyToClipboard(href),
					},
				],
			});
		},
		[flags, formatMessage, getJiraIssuesLink, copyToClipboard],
	);

	const triggerSingleIssueInsertLinkFailureFlag = useCallback(
		(payload: SingleIssueCreatedFlagPayload) => {
			const link = getJiraIssuesLink([{ key: payload.issueKey }]);
			void flags?.showFlag({
				id: FLAG_ID,
				type: 'error',
				title: formatMessage(messages.sidePanelSingleIssueInsertLinkFailureFlagTitle, {
					issueKey: payload.issueKey,
					issueSummary: payload.summary,
					link: (chunks: ReactNode[]) => (
						<Anchor href={link} target="_blank">
							{chunks}
						</Anchor>
					),
				}),
				close: 'auto',
			});
		},
		[flags, formatMessage, getJiraIssuesLink],
	);

	const triggerMultipleIssuesInsertLinkFailureFlag = useCallback(
		(payload: CreateIssueResponse[]) => {
			const href = getJiraIssuesLink(payload);
			void flags?.showFlag({
				id: FLAG_ID,
				type: 'error',
				title: formatMessage(messages.sidePanelMultipleIssuesInsertLinkFailureFlagTitle, {
					issueCount: payload.length,
				}),
				close: 'auto',
				actions: [
					{
						content: formatMessage(messages.sidePanelMultipleIssueCreateFlagViewIssue, {
							issueCount: payload.length,
						}),
						href,
						target: '_blank',
					},
					{
						content: formatMessage(messages.sidePanelMultipleIssueCreateFlagCopyLink),
						onClick: () => void copyToClipboard(href),
					},
				],
			});
		},
		[flags, formatMessage, getJiraIssuesLink, copyToClipboard],
	);

	return {
		triggerSingleIssueCreatedFlag,
		triggerMultipleIssuesCreatedFlag,
		triggerSingleIssueInsertLinkFailureFlag,
		triggerMultipleIssuesInsertLinkFailureFlag,
	};
};
